import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";

import { useLocation } from "react-router-dom";
import { Form, Formik } from "formik";

import {
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  Text,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";

import * as Yup from "yup";
import { CustomSelectWField } from "../CustomSelectWField";
import pt from "date-fns/locale/pt";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import RelatorioService from "../../../main/services/RelatorioService";
import DadosGeograficosService from "../../../main/services/DadosGeograficosService";
import PromotoriaService from "../../../main/services/PromotoriaService";
import ServicoAcolhimentoService from "../../../main/services/ServicoAcolhimentoService";
import { Permission, criancaFunc } from "../../../helpers/Funcionalidades";
import { CustomDatepickerWField } from "../CustomDatepickerWField";

import { format, isValid, parse, isAfter } from "date-fns";
import { useAuth } from "react-oidc-context";
import { IoHelpCircle } from "react-icons/io5";
import { PopoverGenero } from "../PopoverGenero";

const schema = Yup.object().shape({
  tipoRelatorio: Yup.object().nullable().required("Este campo é obrigatório!"),
  dataReferencia: Yup.string()
    .nullable()
    .test("validDate", "Data inválida!", (value) => {
      if (!!value) {
        if (value.length === 10) {
          return isValid(parse(value, "dd/MM/yyyy", new Date()));
        } else {
          return false;
        }
      } else {
        return true;
      }
    })
    .test(
      "dataNascimentoAfterToday",
      "A data informada não pode ser uma data futura!",
      (value) => {
        if (!value) {
          return true;
        } else {
          return !isAfter(parse(value, "dd/MM/yyyy", new Date()), new Date());
        }
      }
    ),
});

export const FiltersRelatorioPendencia = ({
  entidade,
  data,
  setData,
  setLoading,
  roleKeycloack,
  user,
  exclusao,
  setExclusao,
  atualizarDadosTabela,
  loading,
  setTipoRelatorio,
}) => {
  const auth = useAuth();
  const formRef = useRef(null);
  const location = useLocation();
  const initialLoad = useRef(true);
  const token = auth?.user?.access_token;
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [hintPreliminar, setHintPreliminar] = useState(false);
  const placement = useBreakpointValue({ md: "left" });
  const optionsTipoRelatorio = useMemo(
    () => [
      {
        id: 1,
        label: "Acolhidos",
        value: "RELATORIO_PENDENCIAS_ACOLHIDOS",
        tooltip:
          "O relatório retornará pendências relacionadas aos que se encontram na situação de acolhido",
      },
      {
        id: 2,
        label: "Serviço de Acolhimento",
        value: "RELATORIO_PENDENCIAS_SERVICOS_ACOLHIMENTO",
        tooltip:
          "O relatório retornará pendências relacionadas aos serviços de acolhimento com status ativo no sistema.",
      },
    ],
    []
  );

  useEffect(() => {
    setDefaultLocale("pt-br");
    registerLocale("pt-br", pt);
  }, []);

  const [initialValues, setInitialValues] = useState({
    municipioId: "",
    promocaoId: "",
    tipoRelatorio: "",
    servicoAtendimentoId: "",
    dataReferencia: "",
    pageSize: 10,
    page: 0,
  });

  const [municipios, setMunicipios] = useState({
    options: [],
    isLoading: false,
  });
  const [nomesServicosAcolhimento, setNomesServicosAcolhimento] = useState({
    options: [],
    isLoading: false,
  });

  const [promotorias, setPromotorias] = useState({
    options: [],
    isLoading: false,
  });

  const renderizarHint = () => {
    if (isMobile)
      return (
        <PopoverGenero
          isOpen={hintPreliminar}
          setIsOpen={setHintPreliminar}
          flex={true}
          handleClick={() => setHintPreliminar(!hintPreliminar)}
          labelTextoGenero={
            "A pesquisa irá considerar os acolhidos durante a data de referência, porém as pendências exibidas se referem a situação atual da ficha."
          }
        />
      );

    return (
      <Tooltip
        label={
          "A pesquisa irá considerar os acolhidos durante a data de referência, porém as pendências exibidas se referem a situação atual da ficha."
        }
        fontSize="md"
        maxW="400px"
        textAlign={"justify"}
        placement={placement}
      >
        <span className="hint-tooltip">
          <Icon
            aria-label="Aviso de pendente"
            color="#6B6C71"
            as={IoHelpCircle}
            boxSize={{ base: "20px" }}
          />
        </span>
      </Tooltip>
    );
  };

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (sessionStorage.getItem(entidade + "-filter")) {
      const tempObj = JSON.parse(sessionStorage.getItem(entidade + "-filter"));

      if (tempObj.tipoRelatorio) {
        const opt = optionsTipoRelatorio.find(
          (o) => o.value === tempObj.tipoRelatorio.value
        );
        if (opt) {
          tempObj.tipoRelatorio = {
            ...tempObj.tipoRelatorio,
            label: buildOptionLabel(opt.label, opt.tooltip),
          };
        }
      }

      setInitialValues(tempObj);
      setTipoRelatorio && setTipoRelatorio(tempObj.tipoRelatorio || "");
    }
  }, [entidade, setTipoRelatorio, optionsTipoRelatorio]);

  useEffect(() => {
    if (!initialLoad.current) {
      if (formRef.current) {
        formRef.current.handleSubmit();
      }
    } else {
      initialLoad.current = false;
    }
  }, [location]);

  const handleGetMunicipios = () => {
    setMunicipios({ ...municipios, isLoading: true });
    DadosGeograficosService.pesquisarMunicipios(33).then((res) => {
      const optionsComTodos = [{ id: "", nome: "Todos" }, ...res.data];
      setMunicipios({ options: optionsComTodos, isLoading: false });
    });
  };

  const handleGetPromotorias = (props) => {
    setPromotorias({ ...promotorias, isLoading: true });
    PromotoriaService.pesquisarPromotorias({
      municipioId: props.values?.municipioId?.id
        ? props.values?.municipioId?.id
        : "",
    }).then((res) => {
      setPromotorias({ options: res.data, isLoading: false });
    });
  };

  const handleGetNomesServicosAcolhimento = (props) => {
    setNomesServicosAcolhimento({
      ...nomesServicosAcolhimento,
      isLoading: true,
    });

    ServicoAcolhimentoService.pesquisarServicosAcolhimento({
      municipioId: props.values?.municipioId?.id
        ? props.values?.municipioId?.id
        : "",
    }).then((res) => {
      setNomesServicosAcolhimento({ options: res.data, isLoading: false });
    });
  };

  const isValidDate = (value) => {
    if (!!value) {
      if (value.length === 10) {
        return isValid(parse(value, "dd/MM/yyyy", new Date()));
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const funcPesquisarRelatorioPendencia = useCallback(
    (valuesToSend) => {
      let request =
        valuesToSend.tipoRelatorio ===
        "RELATORIO_PENDENCIAS_SERVICOS_ACOLHIMENTO"
          ? RelatorioService.getRelatorioPendenciaServico(token, valuesToSend)
          : RelatorioService.getRelatorioPendenciaCrianca(token, valuesToSend);

      setTipoRelatorio(valuesToSend.tipoRelatorio);
      request.then((res) => {
        sessionStorage.setItem(entidade + "-filterSearch", true);
        const tempObj = JSON.parse(
          sessionStorage.getItem(entidade + "-filter")
        );

        sessionStorage.setItem(
          entidade + "-filter",
          JSON.stringify({
            ...tempObj,
            totalItems: res.data.totalElements,
            totalPages: res.data.totalPages,
          })
        );

        setData(res.data);
        setLoading(false);
      });
    },
    [entidade, setData, setLoading, token, setTipoRelatorio]
  );

  useEffect(() => {
    const tempObj = JSON.parse(sessionStorage.getItem("table-" + entidade));
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    const pesquisou = sessionStorage.getItem(entidade + "-filterSearch");
    const passou = sessionStorage.getItem("crianca-passou");
    const servicoPassou = sessionStorage.getItem("servico-passou");

    if (atualizarDadosTabela || (pesquisou && (passou || servicoPassou))) {
      setLoading(true);

      const formatarData = (data) => {
        if (!data) return "";
        if (data.includes("/")) {
          const [dia, mes, ano] = data.split("/");
          return `${ano}-${mes}-${dia}`;
        }
        return data;
      };

      const valuesToSend = {
        municipioId: storage?.municipioId ? storage.municipioId.id : "",
        promocaoId: storage?.promocaoId ? storage.promocaoId.id : "",
        tipoRelatorio: storage?.tipoRelatorio
          ? storage.tipoRelatorio.value
          : "",
        servicoAtendimentoId: storage?.servicoAtendimentoId
          ? storage?.servicoAtendimentoId.id
          : "",
        dataReferencia: storage?.dataReferencia
          ? formatarData(storage.dataReferencia)
          : "",
        pageSize: storage?.pageSize ? storage.pageSize : "",
        page: storage?.page ? storage.page : "",
        sortField: tempObj ? tempObj[0]?.id : "nomeMunicipio",
        sortType: tempObj ? (tempObj[0]?.desc ? "DESC" : "ASC") : "ASC",
      };

      funcPesquisarRelatorioPendencia(valuesToSend);
    }
  }, [
    exclusao,
    atualizarDadosTabela,
    location.state,
    entidade,
    funcPesquisarRelatorioPendencia,
    setLoading,
  ]);

  const handleSubmit = (values, actions) => {
    const storage = JSON.parse(sessionStorage.getItem(entidade + "-filter"));
    setTimeout(() => {
      const {
        municipioId,
        promocaoId,
        tipoRelatorio,
        servicoAtendimentoId,
        dataReferencia,
      } = values;

      values.page = 0;
      values.pageSize = storage?.pageSize ? storage.pageSize : 10;

      let parsedDataReferencia;
      let formattedDataReferencia;

      if (dataReferencia && isValidDate(dataReferencia)) {
        parsedDataReferencia = parse(dataReferencia, "dd/MM/yyyy", new Date());
        formattedDataReferencia = format(parsedDataReferencia, "yyyy-MM-dd");
      }

      const valuesToSend = {
        municipioId: municipioId ? municipioId.id : "",
        promocaoId: promocaoId ? promocaoId.id : "",
        tipoRelatorio: tipoRelatorio ? tipoRelatorio.value : "",
        servicoAtendimentoId: servicoAtendimentoId
          ? servicoAtendimentoId.id
          : "",
        dataReferencia: formattedDataReferencia ? formattedDataReferencia : "",
        pageSize: storage?.pageSize ? storage.pageSize : 10,
        page: 0,
      };

      if (entidade === "relatorio-pendencia") {
        values.desc = false;
        values.sort = "nomeMunicipio";
        valuesToSend.sortField = "nomeMunicipio";
        valuesToSend.sortType = "ASC";
        sessionStorage.setItem(
          "table-relatorio-pendencia",
          JSON.stringify([{ id: "nomeMunicipio", desc: false }])
        );
      }

      setLoading(true);

      const normalizedValues = {
        ...values,
        tipoRelatorio: tipoRelatorio
          ? {
              id: tipoRelatorio.id,
              value: tipoRelatorio.value,
              labelText:
                optionsTipoRelatorio.find(
                  (opt) => opt.value === tipoRelatorio.value
                )?.label || "",
              tooltip:
                optionsTipoRelatorio.find(
                  (opt) => opt.value === tipoRelatorio.value
                )?.tooltip || "",
            }
          : "",
      };

      sessionStorage.setItem(
        entidade + "-filter",
        JSON.stringify(normalizedValues)
      );
      if (entidade === "relatorio-pendencia") {
        funcPesquisarRelatorioPendencia(valuesToSend);
      }
      actions.setSubmitting(false);
    }, 200);
  };

  const buildOptionLabel = (text, tooltip) => (
    <HStack justifyContent="space-between">
      <Text
        color="#1A203D"
        fontWeight={400}
        fontSize={{ base: "12px", md: "14px" }}
      >
        {text}
      </Text>
      <Tooltip label={tooltip} fontSize="md" textAlign="justify">
        <span className="hint-tooltip">
          <Icon
            aria-label="Aviso de pendente"
            color="#6B6C71"
            as={IoHelpCircle}
            boxSize={{ base: "10px", md: "20px" }}
          />
        </span>
      </Tooltip>
    </HStack>
  );

  return (
    <Flex flexDir="column" alignItems="center" w="100%">
      <Formik
        validationSchema={schema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
        innerRef={formRef}
      >
        {(props) => (
          <Form style={{ width: "100%" }}>
            <Flex flexDirection="column" alignItems="center">
              <Grid
                templateColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(12, 2fr)",
                }}
                rowGap="10px"
                columnGap="18px"
                w="100%"
                mt="2px"
              >
                <>
                  <GridItem colSpan={6}>
                    <Flex alignItems={"end"} gap={2}>
                      <CustomSelectWField
                        fieldName="tipoRelatorio"
                        label="Universo das Pendências*"
                        placeholder="Selecione o Universo das Pendências"
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.id}
                        options={optionsTipoRelatorio.map((opt) => ({
                          id: opt.id,
                          value: opt.value,
                          label: buildOptionLabel(opt.label, opt.tooltip),
                          searchLabel: opt.label,
                        }))}
                        onChange={(value) => {
                          props.setFieldValue("tipoRelatorio", value);
                        }}
                      />

                      {props.values.tipoRelatorio && isMobile && (
                        <PopoverGenero
                          isOpen={isOpen}
                          setIsOpen={setIsOpen}
                          handleClick={handleClick}
                          labelTextoGenero={
                            props?.values?.tipoRelatorio?.label?.props
                              ?.children[1]?.props?.label
                          }
                        />
                      )}
                    </Flex>
                  </GridItem>

                  <GridItem colSpan={6}>
                    <CustomSelectWField
                      fieldName="municipioId"
                      getOptionLabel={(option) => option.nome}
                      getOptionValue={(option) => option.id}
                      isLoading={municipios.isLoading}
                      options={municipios.options}
                      label="Município"
                      placeholder="Selecione Município"
                      isDisabled={!props?.values?.tipoRelatorio}
                      onChange={(value) => {
                        props.setFieldValue("municipioId", value);
                        props.setFieldValue("servicoAtendimentoId", "");
                        props.setFieldValue("promocaoId", "");
                      }}
                      onFocus={
                        municipios.options.length === 0
                          ? handleGetMunicipios
                          : null
                      }
                    />
                  </GridItem>

                  <GridItem colSpan={5}>
                    <CustomSelectWField
                      fieldName="servicoAtendimentoId"
                      getOptionLabel={(option) => option?.nomeFantasia}
                      getOptionValue={(option) => option.id}
                      isLoading={nomesServicosAcolhimento.isLoading}
                      options={nomesServicosAcolhimento.options}
                      label="Serviço de Acolhimento"
                      placeholder="Selecione o Serviço de Acolhimento"
                      isDisabled={!props?.values?.tipoRelatorio}
                      onChange={(value) => {
                        props.setFieldValue("servicoAtendimentoId", value);
                      }}
                      onFocus={() => handleGetNomesServicosAcolhimento(props)}
                    />
                  </GridItem>

                  <GridItem colSpan={{ base: 5, lg: 4, xl: 5 }}>
                    <CustomSelectWField
                      fieldName="promocaoId"
                      getOptionLabel={(option) => option.nome}
                      getOptionValue={(option) => option.id}
                      autoload={false}
                      isLoading={promotorias.isLoading}
                      options={promotorias.options}
                      label="Promotoria"
                      placeholder="Selecione a Promotoria"
                      isDisabled={!props?.values?.tipoRelatorio}
                      onChange={(value) => {
                        props.setFieldValue("promocaoId", value);
                      }}
                      onFocus={() => handleGetPromotorias(props)}
                    />
                  </GridItem>

                  <GridItem
                    colSpan={2}
                    display={"flex"}
                    gap={6}
                    alignItems={"center"}
                  >
                    <Flex gap={2}>
                      <CustomDatepickerWField
                        fieldName="dataReferencia"
                        label="Data de Referência"
                        placeholder="DD/MM/AAAA"
                        isDisabled={!props?.values?.tipoRelatorio}
                        maxDate={new Date()}
                      />
                      <Flex alignSelf="flex-end" pb={3}>
                        {renderizarHint()}{" "}
                      </Flex>
                    </Flex>
                  </GridItem>
                </>
              </Grid>

              <Permission
                roleKeycloack={roleKeycloack}
                func={criancaFunc.pesquisar}
              >
                <Button
                  type="submit"
                  translate="no"
                  color="#FFF"
                  backgroundColor="#169499"
                  fontWeight={700}
                  fontSize="15px"
                  lineHeight="21.6px"
                  _hover={{ bg: "#169499" }}
                  isLoading={props.isSubmitting}
                  mt={6}
                  w={"100%"}
                  maxW={{ base: "100%", md: "50%", lg: "17%" }}
                >
                  Gerar Relatório
                </Button>
              </Permission>
            </Flex>
          </Form>
        )}
      </Formik>
    </Flex>
  );
};
